/**
 * Test script for inventory valuation methods
 * 
 * This script tests the different valuation methods with sample data
 * to verify they produce different results as expected.
 */

import { calculatePMP, calculateFIFO, calculateLIFO, calculateCUMP, calculateWilson } from './valuationMethods';
// Removed mock data import - using real data from API

// Test function
const testValuationMethods = () => {
  console.log('===== TESTING INVENTORY VALUATION METHODS =====');
  
  // Use real stock data from localStorage
  let realMovements = [];
  try {
    const stockItemsData = localStorage.getItem('stockpro_stockItems') || localStorage.getItem('stockItems');
    if (stockItemsData) {
      realMovements = JSON.parse(stockItemsData);
    }
  } catch (error) {
    console.error('Error loading stock data:', error);
    realMovements = [];
  }
  
  // Group movements by product
  const productMovements = {};
  realMovements.forEach(movement => {
    if (!movement || !movement.productId) return;
    
    if (!productMovements[movement.productId]) {
      productMovements[movement.productId] = [];
    }
    productMovements[movement.productId].push(movement);
  });
  
  // Get real products from localStorage
  let sampleProducts = [];
  try {
    const productsData = localStorage.getItem('stockpro_products') || localStorage.getItem('products');
    if (productsData) {
      const allProducts = JSON.parse(productsData);
      // Get first 3 products that have movements
      sampleProducts = allProducts
        .filter(product => productMovements[product.id] && productMovements[product.id].length > 0)
        .slice(0, 3);
      
      // If we don't have enough products with movements, add some fallback products
      if (sampleProducts.length < 3) {
        console.log('Not enough products with movements found, using fallback products');
        while (sampleProducts.length < 3) {
          sampleProducts.push({
            id: `fallback-${sampleProducts.length + 1}`,
            name: `Produit de test ${sampleProducts.length + 1}`,
            price: 1000 * (sampleProducts.length + 1),
            stock: 10 * (sampleProducts.length + 1)
          });
        }
      }
    }
  } catch (error) {
    console.error('Error loading products data:', error);
    sampleProducts = [
      { id: 'fallback-1', name: 'Produit de test 1', price: 1000, stock: 10 },
      { id: 'fallback-2', name: 'Produit de test 2', price: 2000, stock: 20 },
      { id: 'fallback-3', name: 'Produit de test 3', price: 3000, stock: 30 }
    ];
  }
  
  // Test each product with each method
  sampleProducts.forEach(product => {
    console.log(`\n----- Testing Product: ${product.name} (${product.id}) -----`);
    
    // Get movements for this product
    const movements = productMovements[product.id] || [];
    console.log(`Found ${movements.length} movements for this product`);
    
    // Calculate using each method
    const pmpResult = calculatePMP(product, movements);
    const fifoResult = calculateFIFO(product, movements);
    const lifoResult = calculateLIFO(product, movements);
    const cumpResult = calculateCUMP(product, movements);
    const wilsonResult = calculateWilson(product, movements);
    
    // Display results
    console.log('PMP:    ', formatResult(pmpResult));
    console.log('FIFO:   ', formatResult(fifoResult));
    console.log('LIFO:   ', formatResult(lifoResult));
    console.log('CUMP:   ', formatResult(cumpResult));
    console.log('Wilson: ', formatResult(wilsonResult));
    
    // Compare differences
    console.log('\nDifferences between methods:');
    console.log('FIFO vs PMP:   ', calculateDifference(fifoResult, pmpResult));
    console.log('LIFO vs PMP:   ', calculateDifference(lifoResult, pmpResult));
    console.log('CUMP vs PMP:   ', calculateDifference(cumpResult, pmpResult));
    console.log('Wilson vs PMP: ', calculateDifference(wilsonResult, pmpResult));
  });
  
  console.log('\n===== TEST COMPLETED =====');
};

// Helper function to format result
const formatResult = (result) => {
  return {
    unitPrice: parseFloat(result.unitPrice.toFixed(2)),
    totalValue: parseFloat(result.totalValue.toFixed(2)),
    totalQuantity: result.totalQuantity
  };
};

// Helper function to calculate percentage difference
const calculateDifference = (result1, result2) => {
  const priceDiff = ((result1.unitPrice - result2.unitPrice) / result2.unitPrice) * 100;
  const valueDiff = ((result1.totalValue - result2.totalValue) / result2.totalValue) * 100;
  
  return {
    unitPriceDiff: `${priceDiff.toFixed(2)}%`,
    totalValueDiff: `${valueDiff.toFixed(2)}%`
  };
};

export default testValuationMethods;